import { Injectable } from "@angular/core";
import { CountryScopedMetricCostAssumptions, FlatMetricCostAssumptions, MetricCostAssumptions } from "src/app/core/services/ebr-api-client/ebr-api-client.g";
import { CostAssumptionsLayout, ModifiedFormData } from "../types/tvd-types";
import { TVDService } from "../services/tvd.service";
import { FiltersService } from "src/app/core/services/filters.service";

@Injectable({ providedIn: 'root' })
/**
 * This builder constructs the payload for metric cost assumptions based on the form data and layout.
 * It determines whether to build a flat or country-scoped payload using the CostAssumptionsLayoutService.
 */
export class MetricCostAssumptionsPayloadBuilder {

  constructor(
    private _tvdService: TVDService,
    private _filterService: FiltersService
  ) {
  }


  /**
   * Builds the appropriate MetricCostAssumptions payload based on the current layout configuration.
   *
   * This method acts as the single entry point for constructing metric cost assumption payloads.
   * It inspects the resolved layout from {@link CostAssumptionsLayoutService} to determine whether
   * metric values should be treated as flat (not country-specific) or scoped by country.
   *
   * - When the resolved layout is `'byCountry'`, the method expects the input to be keyed by
   *   country codes, where each country maps to a {@link ModifiedFormData} object containing
   *   metric IDs and numeric values.
   * - For all other layouts, the method expects a flat {@link ModifiedFormData} object where
   *   metric IDs map directly to numeric values.
   *
   * The returned object is always a concrete implementation of {@link MetricCostAssumptions}
   * suitable for submission to the EBR API.
   *
   * @param formData
   * An object containing metric values, either:
   * - a flat {@link ModifiedFormData} map of metricId → value, or
   * - a country-scoped map of countryCode → {@link ModifiedFormData}
   *
   * @returns
   * A {@link MetricCostAssumptions} instance whose concrete type depends on the resolved layout:
   * {@link FlatMetricCostAssumptions} or {@link CountryScopedMetricCostAssumptions}.
   */
  public build(
    formData: ModifiedFormData | { [countryCode: string]: ModifiedFormData },
    layout: CostAssumptionsLayout,
  ): MetricCostAssumptions {

    if (layout === 'byCountry') {
      return this.buildCountryScopedMetricCostAssumptionsPayload(formData as { [countryCode: string]: ModifiedFormData });
    } else {
      return this.buildFlatMetricCostAssumptionsPayload(formData as ModifiedFormData);
    }
  }

  /**
   * Builds a FlatMetricCostAssumptions object from the filtered metric IDs.
   * @param formData - An object where each key is a metric ID and the value is the corresponding numeric value from the form controls.
   * @returns A FlatMetricCostAssumptions object containing the metric values.
   */
  private buildFlatMetricCostAssumptionsPayload(formData: ModifiedFormData): FlatMetricCostAssumptions {
    const payload = new FlatMetricCostAssumptions();

    const rates: { [metricId: string]: number } = {};

    Object.entries(formData || {}).forEach(([metricId, rawValue]) => {
      const numericValue = this.parseNumericValue(rawValue);

      if (numericValue === undefined) return;

      rates[metricId] = numericValue;
    });

    payload.rates = rates;
    payload.isEmpty = Object.keys(rates).length === 0;
    return payload;
  }

  /**
   * Builds a CountryScopedMetricCostAssumptions object from the filtered metric IDs.
   * @param formData - An object where each key is a country code and the value is a ModifiedFormData object containing the metric rates for that country.
   * @returns A CountryScopedMetricCostAssumptions object containing the metric values.
   */
  private buildCountryScopedMetricCostAssumptionsPayload(
    formData: { [countryCode: string]: ModifiedFormData },
  ): CountryScopedMetricCostAssumptions {

    const ratesByCountry: Record<string, Record<string, number>> = {};

    // Parse incoming values safely
    Object.entries(formData || {}).forEach(([countryCode, countryRates]) => {
      ratesByCountry[countryCode] = {};

      Object.entries(countryRates || {}).forEach(([metricId, rawValue]) => {
        const numericValue = this.parseNumericValue(rawValue);

        if (numericValue !== undefined) {
          ratesByCountry[countryCode][metricId] = numericValue;
        }
      });
    });

    // Collect ALL metricIds that appear anywhere in formData
    const metricIds = new Set<string>();

    Object.values(ratesByCountry).forEach(countryRates => {
      Object.keys(countryRates || {}).forEach(metricId => {
        metricIds.add(metricId);
      });
    });

    // Ensure each country has all those metrics
    // TODO: Consider encapsulating this logic in the FilterService as getSelectedCountries() just like how the others were encapsulated (getSelectedAccountNos and getBenchmarkOptions), so that the builder can be more focused on building the payload without having to know about the filter service. 
    const countryCodes = this._filterService.selectedFilters?.getValue()?.countries || [];
    for (const countryCode of countryCodes) {

      const existing = ratesByCountry[countryCode] || {};
      const completed: { [metricId: string]: number } = { ...existing };

      for (const metricId of metricIds) {

        // Only fill if missing
        if (completed[metricId] === undefined || completed[metricId] === null) {
          const defaultRate = this._tvdService.getDefaultRate(metricId, countryCode);

          // TODO: Why getDefaultRate supports returning string? Should it be fixed at the source? For now, we attempt to parse it, and if that fails, default to 1 
          const parsedDefault = this.parseNumericValue(defaultRate as any);

          completed[metricId] = parsedDefault ?? 1;
        }
      }

      ratesByCountry[countryCode] = completed;
    }

    const isEmpty = Object.values(ratesByCountry).every(
      countryRates => Object.keys(countryRates || {}).length === 0
    );

    const payload = {
      $type: 'CountryScopedMetricCostAssumptions',
      countryRates: ratesByCountry,
      rates: ratesByCountry,
      isEmpty
    };

    // HACK: The EBR API client expects a concrete class instance, but we are currently building a plain object. To bridge this gap without refactoring the entire builder, we use a type assertion. In the future, consider refactoring this builder to construct actual instances of CountryScopedMetricCostAssumptions for better type safety and maintainability.
    return payload as unknown as CountryScopedMetricCostAssumptions;
  }

  // TODO: Modify ModifiedFormData to support number values only for metric rates, so that we don't have to do this parsing in the first place. The presence of string values in the form data adds unnecessary complexity and potential for bugs, as we have to defensively check and parse values that ideally should already be in the correct numeric format.
  /**
   * Centralized parser for number|string inputs
   */
  private parseNumericValue(value: number | string): number | undefined {
    if (value === null || value === undefined) return undefined;

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return undefined;

      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
  }
}